/**
 * The chart's Vitals & ADL, Measures and Chart tabs. Ported from the design's renderVals
 * (L38496-39277): the resident scorecard, the face sheet, the care-plan clock, MDS, PDPM,
 * the timeline, transfers, vitals against the resident's own baseline, weight (MDS 3.0
 * K0300), ADL levels, immunizations, advance care planning and the measures registry.
 */
import type {
  AdlResponse,
  AdvanceCarePlanningResponse,
  CarePlanResponse,
  ChartResponse,
  FacesheetResponse,
  ImmunizationsResponse,
  MdsResponse,
  PdpmResponse,
  ResidentScoreResponse,
  TimelineResponse,
  TransfersResponse,
  VitalCard,
  VitalsResponse,
} from '@aithora/shared';
import { DASH, DAY, WITHHELD, ago, tag, type ChartWithheld } from './chartVals';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;
const RED = '#B3261E', BLU = '#2F5C97', GRN = '#1F6B52', PUR = '#5B6B7C', SLT = '#54606C', INK = '#22272E';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dmy = (iso: string) => { const d = new Date(iso); return d.getDate() + ' ' + MON[d.getMonth()] + ' ' + d.getFullYear(); };
const dm = (iso: string) => { const d = new Date(iso); return d.getDate() + ' ' + MON[d.getMonth()]; };
const hhmm = (iso: string) => { const d = new Date(iso); return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); };
const days = (iso: string | null | undefined, nowMs: number) => (iso ? Math.round((nowMs - Date.parse(iso)) / DAY) : null);

/** The design's bar spark: one bar per reading, the last in the caller's colour (L38665). */
export function barSpark(arr: number[], color: string, h: number): string[] {
  if (!arr.length) return [];
  const lo = Math.min(...arr), hi = Math.max(...arr), span = hi - lo || 1;
  return arr.map((v, k) =>
    'display:block;flex:1 1 0;min-width:2px;border-radius:1px;background:' + (k === arr.length - 1 ? color : 'rgba(34,39,46,.22)') + ';height:' + Math.max(3, Math.round(4 + ((v - lo) / span) * (h - 4))) + 'px;');
}

// ── Vitals & ADL ──────────────────────────────────────────────────────────────
/** The design's order, its day-to-day spread per vital and its normal ranges (VD, L38672). */
const VITAL_ORDER: Array<VitalCard['metric']> = ['systolic_bp', 'heart_rate', 'respiratory_rate', 'body_temperature', 'oxygen_saturation', 'pain'];
const SPREAD: Record<string, number> = { systolic_bp: 11, heart_rate: 9, respiratory_rate: 3, body_temperature: 0.7, oxygen_saturation: 2.4, pain: 1.8 };
const RANGE: Record<string, [number, number]> = { systolic_bp: [90, 140], heart_rate: [60, 100], respiratory_rate: [12, 20], body_temperature: [97, 99.5], oxygen_saturation: [92, 100], pain: [0, 4] };
const LV = ['Independent', 'Supervision', 'Limited assist', 'Extensive assist', 'Total dependence'];

export function vitalsVals(opts: {
  vitals: VitalsResponse | null;
  vitalsLoading: boolean;
  adl: AdlResponse | null;
  adlError: boolean;
  masked: boolean;
  reload: () => void;
  reloadAdl: () => void;
  nowMs: number;
  withheld?: ChartWithheld;
}) {
  const wh = opts.withheld ?? {};
  const cards = opts.vitals?.vitals.vitals ?? [];
  const rv = VITAL_ORDER.map((m) => cards.find((c) => c.metric === m)).filter((c): c is VitalCard => !!c).map((c) => {
    const series = c.series ?? [];
    const dec = c.decimals ?? (c.metric === 'body_temperature' ? 1 : 0);
    const unit = c.unit ?? '';
    if (c.value == null || (!series.length && !c.count)) {
      return {
        label: c.label, state: 'NOT ASSESSED', stateStyle: tag(SLT, 'rgba(34,39,46,.08)'),
        prov: 'No reading on file. Not the same as normal.',
        latest: DASH, base: DASH, delta: DASH, color: SLT, deltaColor: SLT, spark: [], noSpark: true, noSparkNote: 'no series',
      };
    }
    const latest = c.value;
    const base = c.baseline ?? (series.length ? series.slice(0, 7).reduce((a, b) => a + b, 0) / Math.min(7, series.length) : latest);
    const delta = latest - base;
    const [lowB, hiB] = [c.normalLow ?? RANGE[c.metric]?.[0] ?? -Infinity, c.normalHigh ?? RANGE[c.metric]?.[1] ?? Infinity];
    const out = latest < lowB || latest > hiB;
    const drift = Math.abs(delta) > (SPREAD[c.metric] ?? 1) * 0.45;
    const stale = !!c.staleHours;
    return {
      label: c.label,
      state: stale ? 'SENSOR STALE ' + c.staleHours + 'H' : 'LIVE',
      stateStyle: stale ? tag(PUR, 'rgba(91,107,124,.14)') : tag(GRN, 'rgba(47,138,111,.13)'),
      prov: (c.sourceLabel ?? (c.recordedAt ? 'Recorded ' + ago(c.recordedAt, opts.nowMs) : DASH)) + (stale ? ' · last good read ' + c.staleHours + 'h ago, shown with that caveat' : ''),
      latest: latest.toFixed(dec) + (unit === '%' || unit === '°F' ? unit : ' ' + unit),
      base: base.toFixed(dec),
      delta: (delta >= 0 ? '+' : '−') + Math.abs(delta).toFixed(dec === 0 ? 1 : dec),
      color: out ? RED : INK,
      deltaColor: out ? RED : drift ? PUR : SLT,
      spark: barSpark(series, out ? RED : drift ? PUR : BLU, 26),
      noSpark: !series.length,
      noSparkNote: series.length ? '' : 'no series',
    };
  });

  const w = opts.vitals?.vitals.weight;
  const wNow = w?.value ?? null;
  const p30 = w?.change30Pct ?? null;
  const p180 = w?.change180Pct ?? null;
  const trip30 = p30 != null && p30 <= -5;
  const trip180 = p180 != null && p180 <= -10;
  const wTrip = trip30 || trip180;
  const pct = (v: number | null) => (v == null ? DASH : (v >= 0 ? '+' : '−') + Math.abs(v).toFixed(1) + '%');
  const heightIn = w?.heightIn ?? null;
  const bmi = opts.vitals?.vitals.bmi.value ?? (wNow != null && heightIn ? (wNow / (heightIn * heightIn)) * 703 : null);

  const levels = opts.adl?.adl.levels ?? [];
  const busy = opts.vitalsLoading;
  const out = {
    rvVitals: wh.vitals
      ? [{ label: 'Vitals', state: 'WITHHELD', stateStyle: tag(SLT, 'rgba(34,39,46,.08)'), prov: WITHHELD, latest: DASH, base: DASH, delta: DASH, color: SLT, deltaColor: SLT, spark: [] as string[], noSpark: true, noSparkNote: '' }]
      : rv,
    rvBusy: busy,
    rvReady: !busy,
    rvSkeleton: [92, 78, 86, 70, 88, 64].map((x) => 'display:block;height:16px;width:' + x + '%;border-radius:5px;background:linear-gradient(90deg,rgba(34,39,46,.07),rgba(34,39,46,.13),rgba(34,39,46,.07));animation:bl 1.1s ease-in-out infinite;'),
    rvRefreshLabel: busy ? 'Reading…' : 'Refresh',
    rvRefreshStyle:
      'display:inline-flex;align-items:center;justify-content:center;min-height:30px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (busy ? 'color:#54606C;background:rgba(34,39,46,.06);pointer-events:none;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;'),
    rvRefresh: (e: Ev) => { pre(e); if (!busy) opts.reload(); },
    rvAdlErr: opts.adlError && !wh.adl,
    rvAdlOk: !opts.adlError || !!wh.adl,
    rvAdlErrNote: 'The ADL documentation feed did not return for this resident. Nothing else on the page is affected: this one section failed, and it is not showing you stale values dressed up as current.',
    rvAdlRetry: (e: Ev) => { pre(e); opts.reloadAdl(); },
    rvVitalNote: 'Every figure is this resident against themselves, not a population average. Baseline is the mean of the first seven days in the window. A stale sensor keeps its value and carries the caveat; a vital never assessed shows as not assessed, never as normal.',
    rvWtNow: wNow == null ? DASH : wNow.toFixed(1) + ' lb',
    rvWtColor: wTrip ? RED : INK,
    rvBmi: bmi == null ? DASH : bmi.toFixed(1),
    rvWtUsual: w?.usualLb == null ? DASH : w.usualLb.toFixed(1) + ' lb',
    rvWtSpark: barSpark(w?.seriesLb ?? [], wTrip ? RED : BLU, 46),
    rvWtFlag: wNow == null ? 'NOT ASSESSED' : wTrip ? 'SIGNIFICANT WEIGHT LOSS' : 'WITHIN THRESHOLD',
    rvWtFlagStyle: wNow == null ? tag(SLT, 'rgba(34,39,46,.08)') : wTrip ? tag(RED, 'rgba(179,38,30,.12)') : tag(GRN, 'rgba(47,138,111,.13)'),
    rvWtRules: [
      { k: '30-day change', v: pct(p30), color: trip30 ? RED : INK, note: 'threshold 5% · MDS 3.0 K0300' },
      { k: '180-day change', v: pct(p180), color: trip180 ? RED : INK, note: 'threshold 10% · MDS 3.0 K0300' },
    ],
    rvWtProv:
      (wNow == null
        ? 'No weight on file for this resident.'
        : 'Weights recorded by care staff on the unit scale · last reading ' + (w?.lastDaysAgo ?? days(w?.recordedAt, opts.nowMs) ?? DASH) + 'd ago.' + (heightIn ? ' Height ' + heightIn + ' in, recorded at admission.' : '')) +
      (opts.masked ? ' Resident is outside your facility: name, age and room are withheld.' : ''),
    rvAdl: wh.adl ? [{ label: 'ADL self-performance', now: WITHHELD, base: '', color: SLT, trend: 'withheld', trendStyle: tag(SLT, 'rgba(34,39,46,.07)') }] : levels.map((a) => {
      const worse = a.now > a.baseline;
      return {
        label: a.label, now: LV[a.now], base: 'baseline ' + LV[a.baseline].toLowerCase(),
        color: worse ? PUR : INK,
        trend: worse ? 'more assist than baseline' : 'stable',
        trendStyle: worse ? tag(PUR, 'rgba(91,107,124,.14)') : tag(SLT, 'rgba(34,39,46,.07)'),
      };
    }),
    rvAdlProv: wh.adl ? WITHHELD + '.' : 'ADL self-performance documented per shift by care staff' + (opts.adl?.adl.lastEntryLabel ? ' · last entry ' + opts.adl.adl.lastEntryLabel : '') + '. Levels follow the MDS 3.0 scale.',
  };
  return wh.vitals
    ? { ...out, rvWtNow: DASH, rvWtFlag: 'WITHHELD', rvWtFlagStyle: tag(SLT, 'rgba(34,39,46,.08)'), rvWtProv: WITHHELD + '.' }
    : out;
}

// ── Measures registry (design L38922) ─────────────────────────────────────────
export interface MeasuresUi {
  domain: string;
  track: 'all' | 'DRIVE' | 'DETECT';
  query: string;
}
export function measuresVals(opts: {
  chart: ChartResponse | null;
  ui: MeasuresUi;
  setUi: (p: Partial<MeasuresUi>) => void;
  capture: (measureId: string, drive: boolean) => void;
  canWrite: boolean;
  nowMs: number;
}) {
  const doms = (opts.chart?.chart.domains ?? []).slice().sort((a, b) => a.order - b.order);
  const tg = tag;
  const all = doms.flatMap((d, di) => d.measures.map((m) => ({ d, di, m }))).sort((a, b) => (a.m.seq ?? 0) - (b.m.seq ?? 0));
  const rows = all.map(({ d, di, m }) => {
    const trig = m.triggers[0] ?? null;
    const kind = trig ? (trig.mode === 'drive' ? 'DRIVE' : 'DETECT') : 'DETECT';
    const state = m.state;
    const label = state === 'recorded' ? 'CAPTURED' : state === 'confirmed_negative' ? 'NOT APPLICABLE' : 'NOT ASSESSED';
    const color = state === 'recorded' ? GRN : state === 'confirmed_negative' ? SLT : PUR;
    const bg = state === 'recorded' ? 'rgba(47,138,111,.13)' : state === 'confirmed_negative' ? 'rgba(34,39,46,.07)' : 'rgba(91,107,124,.14)';
    const who = m.recordedBy ?? 'staff';
    const prov = state === 'recorded'
      ? 'Recorded ' + (m.recordedAt ? ago(m.recordedAt, opts.nowMs).replace(/^1m ago$/, 'just now') : DASH) + ' · ' + who
      : state === 'confirmed_negative'
        ? m.why || 'Reviewed and does not apply to this resident · ' + who
        : 'Never assessed for this resident. Not the same as not applicable.';
    const actable = state === 'not_assessed' && opts.canWrite;
    const rate = m.rateLabel ?? 'no fee held';
    return {
      di,
      kind,
      state,
      row: {
        measure: m.label,
        sub: d.label + (m.capture ? ' · Capture: ' + m.capture : '') + (m.aithora ? ' · Aithora: ' + m.aithora : ''),
        code: trig ? trig.code : DASH,
        rate,
        rateColor: rate.indexOf('*') >= 0 ? PUR : rate === 'no fee held' ? SLT : INK,
        kind,
        kindStyle: kind === 'DRIVE' ? tg(BLU, 'rgba(47,92,151,.13)') : tg(PUR, 'rgba(91,107,124,.14)'),
        state: label,
        stateStyle: tg(color, bg),
        prov,
        act: state === 'not_assessed' ? (kind === 'DRIVE' ? 'Capture' : 'Assess') : 'Open',
        actStyle:
          'display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (state === 'not_assessed' ? 'background:#2F5C97;color:#fff;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;'),
        onAct: (e: Ev) => { pre(e); if (actable) opts.capture(m.id, kind === 'DRIVE'); },
      },
    };
  });
  const { domain: dom, track: trk } = opts.ui;
  const q = opts.ui.query.trim().toLowerCase();
  const visible = rows.filter((b) => (dom === 'all' || String(b.di) === dom) && (trk === 'all' || b.kind === trk) && (!q || b.row.measure.toLowerCase().indexOf(q) >= 0 || b.row.code.toLowerCase().indexOf(q) >= 0));
  const nCap = rows.filter((b) => b.state === 'recorded').length;
  const nOpen = rows.filter((b) => b.state === 'not_assessed').length;
  const openDrive = rows.filter((b) => b.state === 'not_assessed' && b.kind === 'DRIVE').length;
  const RND: Record<string, 'status' | 'threshold' | 'flags'> = { '2': 'status', '1': 'threshold', '5': 'threshold', '3': 'flags', '4': 'flags', '6': 'flags', '7': 'flags' };
  const vmode = dom === 'all' ? 'table' : RND[dom] || 'table';
  const META: Record<string, [string, string]> = {
    table: ['REGISTRY TABLE', 'one registry · the domain filters the rows, it does not fork the page'],
    status: ['STATUS LIST', 'one row per measure · given, declined, or never assessed'],
    threshold: ['THRESHOLD LIST', 'the rule that fires the measure · the series themselves live on Vitals & ADL'],
    flags: ['FLAG LIST', 'ordered by what is unassessed first · a flag says go look, never what is wrong'],
  };
  const ORD: Record<string, number> = { not_assessed: 0, recorded: 1, confirmed_negative: 2 };
  const seq = vmode === 'flags' ? visible.slice().sort((a, b) => ORD[a.state] - ORD[b.state]) : visible;
  return {
    rxDomains: [{ value: 'all', label: 'All domains · ' + doms.length }].concat(doms.map((d, n) => ({ value: String(n), label: d.label + ' · ' + d.measures.length }))),
    rxDomain: dom,
    rxDomainSet: (e: { target: { value: string } }) => opts.setUi({ domain: e.target.value }),
    rxTracks: [{ value: 'all', label: 'DRIVE and DETECT' }, { value: 'DRIVE', label: 'DRIVE only' }, { value: 'DETECT', label: 'DETECT only' }],
    rxTrack: trk,
    rxTrackSet: (e: { target: { value: string } }) => opts.setUi({ track: e.target.value as MeasuresUi['track'] }),
    rxQuery: opts.ui.query,
    rxQuerySet: (e: { target: { value: string } }) => opts.setUi({ query: e.target.value }),
    rxCount: visible.length + ' of ' + rows.length + ' measures',
    rxIntro: 'Ten domains, one row per tracked measure. A code is an output of a measure firing, never a tab of its own: adding something new to track is a row here. DRIVE is what Aithora can legitimately document and capture; DETECT is what the facility performs, which we flag and quantify but never bill.',
    rxStats: [
      { k: 'Captured', v: String(nCap), color: GRN, note: 'recorded for this resident, with provenance' },
      { k: 'Not assessed', v: String(nOpen), color: PUR, note: 'never assessed: not the same as not applicable' },
      { k: 'Open DRIVE', v: String(openDrive), color: BLU, note: 'capturable with documentation Aithora can assist' },
      { k: 'Domains', v: String(doms.length), color: INK, note: 'fixed set · the registry grows, the navigation does not' },
    ],
    rxBodyTitle: dom === 'all' ? 'All measures' : doms[Number(dom)]?.label ?? 'All measures',
    rxBodyKind: META[vmode][0],
    rxBodyKindStyle: tag(BLU, 'rgba(47,92,151,.12)'),
    rxBodyMeta: META[vmode][1],
    rxRows: seq.map((b) => b.row),
    rxIsTable: true,
    rxIsCards: false,
    rxCards: [],
    rxEmpty: visible.length === 0,
    rxEmptyNote: 'No measure matches that filter. Clear the search or pick another domain.',
    rxFooter: 'Rates marked * are RVU-derived estimates, not yet reconciled to the CY2026 RVU file. Rows with no code are tracked for care and acuity: they feed MDS, PDPM and the scorecard even where no fee exists in our catalog. Capturing a measure writes to the resident timeline and the ledger; it never bills a DETECT item.',
  };
}

// ── Chart tab: face sheet, scorecard, immunizations, ACP, plan, MDS, PDPM, timeline ──
const GC: Record<string, string> = { A: '#1F6B52', B: '#2F5C97', C: '#5B6B7C', D: '#B3261E', F: '#B3261E' };
const ordinal = (n: number) => n + (n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 ? 'rd' : 'th');
const DISCIPLINE: Record<string, string> = { nursing: 'Nursing', companion: 'Atlas', care_staff: 'Care staff', therapy: 'Therapy', physician: 'Physician', restorative: 'Restorative', dietary: 'Dietary', social_work: 'Social work' };
const VAX = ['Influenza', 'Pneumococcal', 'Shingles (RZV)', 'COVID-19'];

export function recordVals(opts: {
  name: string;
  masked: boolean;
  facility: string;
  facesheet: FacesheetResponse | null;
  score: ResidentScoreResponse | null;
  carePlan: CarePlanResponse | null;
  mds: MdsResponse | null;
  pdpm: PdpmResponse | null;
  timeline: TimelineResponse | null;
  transfers: TransfersResponse | null;
  immunizations: ImmunizationsResponse | null;
  acp: AdvanceCarePlanningResponse | null;
  goalMet: (goalId: string) => void;
  goalsDone: Record<string, boolean>;
  vaxRecord: (vaccine: string) => void;
  canWrite: boolean;
  nowMs: number;
  withheld?: ChartWithheld;
}) {
  const { nowMs } = opts;
  const fsWithheld = !!opts.withheld?.facesheet;
  const chip = (c: string, bg: string) => 'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.07em;font-weight:700;white-space:nowrap;color:' + c + ';background:' + bg + ';';
  const fs = opts.facesheet?.facesheet ?? null;
  const payerType = fs?.payer.type ?? null;
  const isSkilled = !!payerType && /part a/i.test(payerType);
  const payer = fsWithheld ? WITHHELD : payerType ? payerType + (isSkilled && fs?.payer.partADay != null ? ' · day ' + fs.payer.partADay + ' of ' + (fs.payer.partAOf ?? 100) : '') : DASH;
  const code = fsWithheld ? WITHHELD : fs?.codeStatus ?? DASH;
  const full = /^full/i.test(code);
  const algState = fs?.allergyState ?? (fs ? (fs.allergies.length ? 'recorded' : 'not_assessed') : 'not_assessed');
  const allergies = fsWithheld ? WITHHELD : algState === 'affirmed_none' ? 'NKDA, confirmed' : algState === 'not_assessed' ? 'Not yet assessed' : (fs?.allergies ?? []).join(', ');
  const recAgo = fs?.allergyRecordedAt ? ago(fs.allergyRecordedAt, nowMs) : DASH;
  const rep = fs?.representative;
  const repTxt = opts.masked || fsWithheld
    ? (fsWithheld ? WITHHELD : 'Withheld: resident outside your facility')
    : rep?.name
      ? [rep.name, rep.relation, rep.isPoa ? 'POA' : null].filter(Boolean).join(' · ')
      : 'No representative on file';

  // scorecard
  const sc = opts.score?.score ?? null;
  const peers = sc?.peerN ?? 0;
  const thin = peers < 30;
  const comp = sc?.composite ?? null;
  const grade = sc?.grade ?? null;

  // care plan clock
  const cp = opts.carePlan?.carePlan ?? null;
  const admittedAt = cp?.admittedAt ?? fs?.admittedAt ?? null;
  const age = days(admittedAt, nowMs);
  const reviewDue = cp?.nextReviewDueAt ? Math.max(0, Math.round((Date.parse(cp.nextReviewDueAt) - nowMs) / DAY)) : null;
  const planRows: Array<[string, string, string, string]> = cp
    ? [
        ['Baseline care plan', cp.baseline.completedAt ? 'complete' : 'DUE within 48h of admission', cp.baseline.completedAt ? GRN : RED, '42 CFR §483.21(a) · no weekend or holiday exception'],
        ['Comprehensive plan', cp.comprehensive.completedAt ? 'complete' : cp.comprehensive.overdue ? 'overdue' : 'due day 21 · ' + (age == null ? DASH : Math.max(0, 21 - age)) + 'd left', cp.comprehensive.completedAt ? GRN : PUR, 'within 21 days of admission'],
        ['Quarterly revision', reviewDue == null ? DASH : reviewDue > 0 ? 'due in ' + reviewDue + 'd' : 'OVERDUE', reviewDue == null ? SLT : reviewDue > 14 ? GRN : reviewDue > 0 ? PUR : RED, 'revise at least every 3 months, and on any change'],
        ['Summary given to representative', !rep?.name ? 'n/a · none on file' : cp.summaryGivenAt ? 'given ' + days(cp.summaryGivenAt, nowMs) + 'd ago' : 'not yet given', !rep?.name ? '#54606C' : cp.summaryGivenAt ? GRN : PUR, 'goals, medications, dietary, services'],
      ]
    : [];
  const md = opts.mds?.mds ?? null;
  const mdsRows: Array<[string, string, string]> = md
    ? [
        ['Last completed', md.last ? (md.last.type ?? DASH) + ' · ' + (md.last.completedAt ? ago(md.last.completedAt, nowMs) : DASH) : 'none on file', INK],
        ['Next ARD', md.daysUntilNextArd == null ? DASH : md.daysUntilNextArd < 0 ? 'overdue' : 'in ' + md.daysUntilNextArd + ' days', md.daysUntilNextArd != null && md.daysUntilNextArd <= 7 ? PUR : INK],
        ['Look-back window', md.lookbackDays + ' days from the ARD', '#54606C'],
        ['Transmitted to CMS', md.transmission?.state ?? 'not transmitted', md.transmission?.state === 'accepted' ? GRN : PUR],
        ['Significant-change flag', md.significantChange.flagged ? 'review indicated' + (md.significantChange.reason ? ' · ' + md.significantChange.reason : '') : 'none', md.significantChange.flagged ? PUR : '#54606C'],
      ]
    : [];
  const pd = opts.pdpm?.pdpm ?? null;
  const comps = pd?.components ?? null;

  const tl = (opts.timeline?.timeline.entries ?? []).map((t) => {
    const d = new Date(t.at);
    const today = new Date(nowMs);
    const dd = Math.round((new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / DAY);
    const when = (dd <= 0 ? 'Today' : dd === 1 ? 'Yesterday' : dd + 'd ago') + ' · ' + hhmm(t.at);
    const disc = String(t.discipline).toLowerCase();
    const color = String(t.severity ?? '').toLowerCase() === 'high' ? RED : disc === 'companion' ? GRN : disc === 'physician' ? PUR : BLU;
    return {
      when,
      who: DISCIPLINE[disc] ?? t.discipline,
      what: t.title ?? t.kind,
      body: t.body ?? '',
      dot: 'display:block;width:9px;height:9px;border-radius:50%;flex:0 0 9px;margin-top:5px;background:' + color + ';',
      whoStyle: 'display:inline-flex;align-items:center;height:18px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;white-space:nowrap;color:' + color + ';background:rgba(34,39,46,.06);',
    };
  });
  const transfers = (opts.transfers?.transfers.transfers ?? []).map((t) => ({
    when: dmy(t.at),
    where: t.destination ?? DASH,
    body: [t.reason, t.interventions].filter(Boolean).join(' '),
    days: (t.daysOut ?? DASH) + ' days out',
  }));

  // immunizations
  const vx = opts.immunizations?.items ?? [];
  let vaxUp = 0, vaxOpen = 0;
  const vax = VAX.map((label) => {
    const it = vx.find((x) => x.vaccine === label) ?? null;
    const state = it?.state ?? 'not_assessed';
    if (state === 'given') vaxUp += 1;
    if (state === 'not_assessed') vaxOpen += 1;
    const detail = it?.detail ?? '';
    return {
      label,
      prov: state === 'given'
        ? 'Given ' + (it?.givenAt ? days(it.givenAt, nowMs) + 'd ago' : DASH) + ' · ' + (it?.source ?? DASH) + ' · ' + detail
        : state === 'declined' ? 'Offered and declined by the resident · ' + detail : 'No record from the facility or the state registry' + (detail ? ' · ' + detail : ''),
      state: state === 'given' ? 'GIVEN' : state === 'declined' ? 'DECLINED' : 'NOT ASSESSED',
      stateStyle: tag(state === 'given' ? GRN : state === 'declined' ? SLT : PUR, state === 'given' ? 'rgba(47,138,111,.13)' : state === 'declined' ? 'rgba(34,39,46,.07)' : 'rgba(91,107,124,.14)'),
      act: state === 'not_assessed' && opts.canWrite ? 'Record' : 'Open',
      actStyle:
        'display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (state === 'not_assessed' && opts.canWrite ? 'background:#2F5C97;color:#fff;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;'),
      onAct: (e: Ev) => { pre(e); if (state === 'not_assessed' && opts.canWrite) opts.vaxRecord(label); },
    };
  });

  // advance care planning
  const a = opts.acp;
  const on = (v: boolean | null | undefined, yes: string, no: string, yesC: string) => ({
    val: v ? yes : no,
    valStyle: tag(v ? yesC : PUR, v ? (yesC === INK ? 'rgba(34,39,46,.07)' : 'rgba(47,138,111,.14)') : 'rgba(91,107,124,.14)'),
  });
  const acpCode = a?.codeStatus ?? fs?.codeStatus ?? null;
  const acpFull = acpCode ? /^full/i.test(acpCode) : true;
  const talkedDays = days(a?.goalsConversationAt, nowMs);
  const acp = [
    { label: 'Code status', note: 'as documented on the face sheet', val: acpCode ? (acpFull ? 'FULL CODE' : acpCode.toUpperCase()) : 'NOT ASSESSED', valStyle: acpCode ? tag(acpFull ? GRN : RED, acpFull ? 'rgba(47,138,111,.14)' : 'rgba(179,38,30,.12)') : tag(PUR, 'rgba(91,107,124,.14)') },
    { label: 'POLST / MOLST', note: a?.polstOnFile ? 'portable medical order, travels with the resident' : 'no portable order on file', ...on(a?.polstOnFile, 'ON FILE', 'NOT ON FILE', GRN) },
    { label: 'Healthcare proxy', note: a?.proxyNamed ? 'named decision-maker, verified' : 'no proxy named: blank is not the same as none', ...on(a?.proxyNamed, 'NAMED', 'NOT ASSESSED', GRN) },
    { label: 'Goals-of-care conversation', note: talkedDays != null ? 'last held ' + talkedDays + 'd ago' : 'never documented for this resident', ...on(talkedDays != null, talkedDays + 'd ago', 'NOT ASSESSED', INK) },
    { label: 'What matters most at the end of life', note: 'the resident’s own words, kept with the life story', ...on(a?.whatMattersRecorded, 'RECORDED', 'NOT ASSESSED', GRN) },
  ];

  return {
    rChartRoom: opts.masked ? DASH : fs?.room ?? DASH,
    rChartAdmitted: admittedAt ? dmy(admittedAt) : DASH,
    rChartLos: (fs?.lengthOfStayDays ?? age ?? DASH) + ' days',
    rChartPayer: payer,
    rChartPayerStyle: chip(isSkilled ? BLU : '#54606C', isSkilled ? 'rgba(79,138,214,.14)' : 'rgba(34,39,46,.07)'),
    rChartCode: code,
    rChartCodeStyle: chip(full ? GRN : RED, full ? 'rgba(47,138,111,.14)' : 'rgba(179,38,30,.12)'),
    rChartAllergies: allergies,
    rChartAllergyStyle: chip(algState === 'recorded' ? RED : algState === 'affirmed_none' ? GRN : SLT, algState === 'recorded' ? 'rgba(179,38,30,.12)' : algState === 'affirmed_none' ? 'rgba(47,138,111,.14)' : 'rgba(34,39,46,.07)'),
    rChartAllergyProv: algState === 'not_assessed'
      ? 'No allergy assessment on file. Blank is not the same as none.'
      : algState === 'affirmed_none'
        ? 'Affirmed no known drug allergies · ' + (fs?.allergyRecordedBy ?? DASH) + ' · ' + recAgo
        : 'Recorded by ' + (fs?.allergyRecordedBy ?? DASH) + ' · ' + recAgo,
    rChartDx: (fs?.problems ?? []).map((p) => ({ label: p.description ?? p.icd10 ?? DASH })),
    rChartRep: repTxt,

    rScoreComposite: comp == null ? DASH : String(comp),
    rScoreGrade: grade ?? DASH,
    rScoreGradeStyle: 'display:flex;align-items:center;justify-content:center;width:44px;height:44px;flex:0 0 44px;border-radius:10px;font-size:22px;font-weight:800;color:#fff;background:' + (grade ? GC[grade] : '#54606C') + ';',
    rScoreLead: peers >= 30 ? opts.name + ' against the ' + peers + ' residents at ' + opts.facility : opts.name + ' at ' + opts.facility + ' · ' + peers + ' residents, below the reporting floor',
    rScoreBadge: peers >= 30 ? 'MODELED' : 'INSUFFICIENT n',
    rScoreBadgeStyle: 'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.07em;font-weight:700;white-space:nowrap;color:#5B6B7C;background:rgba(91,107,124,.14);',
    rScoreCol3: peers >= 30 ? 'vs building' : '',
    rScoreRows: (sc?.domains ?? []).map((d) => {
      const p = d.percentile;
      const noRank = thin || p == null;
      const c = noRank ? '#54606C' : p >= 60 ? '#1F6B52' : p >= 35 ? '#5B6B7C' : '#B3261E';
      return {
        label: d.label,
        note: thin ? 'peer group too small to rank' : d.basis,
        showTrack: !noRank,
        val: d.score == null ? DASH : String(d.score),
        pctl: noRank ? 'n/a' : ordinal(p),
        pctlStyle: 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;white-space:nowrap;flex:0 0 auto;text-align:right;color:' + c + ';',
        markStyle: noRank ? 'display:none;' : 'position:absolute;top:-3px;width:9px;height:13px;border-radius:3px;margin-left:-4px;background:' + c + ';left:' + p + '%;',
      };
    }),
    rScoreNote: peers >= 30
      ? 'Percentiles are within ' + opts.facility + ' only · ' + peers + ' residents'
      : 'Insufficient n · ' + opts.facility + ' has ' + peers + ' residents, below the 30-resident reporting floor. The domain scores are real; the ranking is withheld rather than computed on a peer group too small to mean anything.',

    rPlanRows: planRows.map((p) => ({ label: p[0], val: p[1], note: p[3], valStyle: 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;white-space:nowrap;color:' + p[2] + ';' })),
    rPlanGoals: (cp?.goals ?? []).map((g) => {
      const gdone = g.state === 'met' || !!opts.goalsDone[g.id];
      const st = gdone ? 'met · documented' : g.state === 'on_track' ? 'on track' : 'not progressing';
      const good = gdone || g.state === 'on_track';
      return {
        text: g.text,
        due: g.dueAt ? 'by ' + dm(g.dueAt) : 'no due date',
        state: st,
        stateStyle: chip(good ? GRN : PUR, good ? 'rgba(47,138,111,.14)' : 'rgba(91,107,124,.14)'),
        doneLabel: opts.goalsDone[g.id] ? 'Documented just now' : gdone ? 'Documented' : 'Mark met',
        doneStyle:
          'display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (gdone ? 'color:#1F6B52;background:rgba(47,138,111,.13);pointer-events:none;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;'),
        onDone: (e: Ev) => { pre(e); if (!gdone && opts.canWrite) opts.goalMet(g.id); },
      };
    }),
    rMdsRows: mdsRows.map((m) => ({ label: m[0], val: m[1], valStyle: 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;white-space:nowrap;color:' + m[2] + ';' })),
    rMdsNote: 'MDS 3.0 v1.20.1 · ' + (md?.lookbackDays ?? 7) + '-day look-back from the ARD',
    rPdpm: comps ? [['PT', comps.pt], ['OT', comps.ot], ['SLP', comps.slp], ['Nursing', comps.nursing], ['NTA', comps.nta]].map((p) => ({ label: p[0] as string, code: p[1] ?? DASH })) : [],
    rPdpmShown: !!pd?.applicable && !!comps,
    rPdpmHidden: !(pd?.applicable && comps),
    rPdpmHiddenNote: pd && !pd.applicable ? 'Not a Part A stay · no PDPM components apply' : 'No PDPM classification on file.',
    rTimeline: tl,
    rSkilledNote: isSkilled ? 'Part A · note required every 24h' : 'Not Part A · no 24h note requirement',
    rTransfers: transfers,
    rTransfersEmpty: transfers.length === 0,
    rVax: vax,
    rVaxSummary: vaxUp + ' of ' + VAX.length + ' given',
    rVaxSummaryStyle: tag(vaxOpen === 0 ? GRN : PUR, vaxOpen === 0 ? 'rgba(47,138,111,.13)' : 'rgba(91,107,124,.14)'),
    rVaxNote: 'Status reconciles the facility record against the state immunization registry. A missing record is shown as not assessed, never as not indicated.',
    rAcp: acp,
    rAcpAge: talkedDays != null ? 'last reviewed ' + talkedDays + 'd ago' : 'never reviewed',
    rAcpNote: 'Code status alone is not a plan. A portable order, a named proxy and a documented conversation are what a receiving hospital actually acts on.',
  };
}
